import {isUndefined} from 'lodash';

import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
  RAnalyticsGraphMetricsEnum,
  RAnalyticsHeatMapMetricsEnum,
  RAnalyticsScalarMetricsEnum,
  RAnalyticsTableMetricsEnum,
} from './analyticsMetricsModel';
import {AnalyticsTimelineEventsEnum} from './analyticsTimelineEventModel';

/*
 * This module contains mappings between metrics (and sub-metrics) to events which should automatically be shown in
 * the Analytics Conversation Timeline. Normally all events except outbound and out_reply events are hidden, but
 * the below mapping enables additional events to be displayed by default.
 *
 * i.g. If we have a metric which shows the percentage of tagged conversations (such as pct_tagged_conversations)
 * we may only want to show "tag" events in the timeline.
 */

/*
 * Types.
 */

// Mapping of scalar metrics to shown events. There are no sub-metrics for scalars.
type ScalarToShownEvents = {
  [key in AnalyticsScalarMetricsEnum]: ReadonlyArray<AnalyticsTimelineEventsEnum>;
};

// Mapping of table metrics to shown events. We must account for sub-metrics with tables.
type TableToShownEvents = {
  [key in AnalyticsTableMetricsEnum]: {
    [key: string]: ReadonlyArray<AnalyticsTimelineEventsEnum> | undefined;
  };
};

// Mapping of graph metrics to shown events. There are no sub-metrics for graphs.
type GraphToShownEvents = {
  [key in AnalyticsGraphMetricsEnum]: ReadonlyArray<AnalyticsTimelineEventsEnum>;
};

// Mapping of heatmap metrics to shown events. We must account for sub-metrics for heatmaps.
type HeatmapToShownEvents = {
  [key in AnalyticsHeatMapMetricsEnum]: {
    [key: string]: ReadonlyArray<AnalyticsTimelineEventsEnum> | undefined;
  };
};

/*
 * Metric to shown event maps.
 */

export const scalarToShownEvents: ScalarToShownEvents = {
  [AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS]: [],
  [AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES]: [],
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS]: [
    AnalyticsTimelineEventsEnum.ARCHIVE,
    AnalyticsTimelineEventsEnum.REOPEN,
  ],
  [AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY]: [
    AnalyticsTimelineEventsEnum.ARCHIVE,
    AnalyticsTimelineEventsEnum.REOPEN,
  ],
  [AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME]: [],
  [AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME]: [],
  [AnalyticsScalarMetricsEnum.AVG_REACTION_TIME]: [],
  [AnalyticsScalarMetricsEnum.AVG_REPLIES_TO_RESOLUTION]: [],
  [AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME]: [],
  [AnalyticsScalarMetricsEnum.AVG_RESOLUTION_TIME]: [],
  // TODO: Apply only for breach tags.
  [AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME]: [AnalyticsTimelineEventsEnum.TAG],
  [AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME]: [
    AnalyticsTimelineEventsEnum.ARCHIVE,
    AnalyticsTimelineEventsEnum.REOPEN,
  ],
  [AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED]: [],
  [AnalyticsScalarMetricsEnum.MESSAGES_SENT]: [],
  [AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT]: [],
  [AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER]: [],
  // TODO: Add support for comments to the Analytics Timeline, and account for CSAT comments here.
  [AnalyticsScalarMetricsEnum.NUM_CSAT_SURVEY_RESPONSE]: [],
  [AnalyticsScalarMetricsEnum.NUM_NEW_SEGMENTS]: [],
  [AnalyticsScalarMetricsEnum.NUM_RESOLVED_SEGMENTS]: [],
  [AnalyticsScalarMetricsEnum.NUM_UNRESOLVED_ACTIVE_SEGMENTS]: [],
  // TODO: Add support for comments to the Analytics Timeline, and account for CSAT comments here.
  [AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION]: [],
  // TODO: Add support for comments to the Analytics Timeline, and account for Message Templates here.
  [AnalyticsScalarMetricsEnum.PCT_MESSAGES_WITH_MESSAGE_TEMPLATES]: [],
  [AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED]: [],
  [AnalyticsScalarMetricsEnum.PCT_TAGGED_CONVERSATIONS]: [AnalyticsTimelineEventsEnum.TAG],
  [AnalyticsScalarMetricsEnum.PCT_RESOLVED_ON_FIRST_REPLY]: [],
  [AnalyticsScalarMetricsEnum.PCT_RESOLVED_SEGMENTS]: [],
  // TODO: Apply only for breach tags.
  [AnalyticsScalarMetricsEnum.SLA_BREACHES]: [AnalyticsTimelineEventsEnum.TAG],
  [AnalyticsScalarMetricsEnum.SLA_WARNINGS]: [],
};

export const tableToShownEvents: TableToShownEvents = {
  [AnalyticsTableMetricsEnum.ACCOUNTS_TABLE]: {},
  [AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2]: {},
  // TODO: Add support for comments to the Analytics Timeline, and account for CSAT comments for the CSAT table metrics.
  [AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO]: {},
  [AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE]: {
    timestamp: [],
    csat_survey_score: [],
  },
  [AnalyticsTableMetricsEnum.INBOX_TABLE]: {
    open_at_start: [AnalyticsTimelineEventsEnum.ARCHIVE],
    moved_out_of_inbox: [AnalyticsTimelineEventsEnum.MOVE],
    moved_into_inbox: [AnalyticsTimelineEventsEnum.MOVE],
    archived_in_inbox: [AnalyticsTimelineEventsEnum.ARCHIVE, AnalyticsTimelineEventsEnum.REOPEN],
    archived_in_inbox_with_reply: [AnalyticsTimelineEventsEnum.ARCHIVE, AnalyticsTimelineEventsEnum.REOPEN],
  },
  [AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE]: {
    num_messages_sent: [AnalyticsTimelineEventsEnum.OUTBOUND],
    num_replies_sent: [AnalyticsTimelineEventsEnum.OUTBOUND],
    avg_handle_time: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
    customer_reply_rate: [AnalyticsTimelineEventsEnum.INBOUND],
  },
  [AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM]: {},
  [AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM]: {},
  [AnalyticsTableMetricsEnum.AVG_REPLIES_TO_RESOLUTION_HISTO]: {},
  [AnalyticsTableMetricsEnum.SANKEY_TABLE]: {
    unrepliedFromPeriod: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    unrepliedFromBacklog: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    unreplied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    repliedFromPeriod: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    repliedFromBacklog: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    replied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    periodNew: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    openFromUnreplied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    openFromReplied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    openAtEnd: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    closedFromUnreplied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    closedFromReplied: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    closedAtEnd: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
    backlogOpen: [
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
      AnalyticsTimelineEventsEnum.MOVE,
    ],
  },
  [AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2]: {},
  [AnalyticsTableMetricsEnum.TAGS_ALL_TABLE]: {
    pct_active_segments: [AnalyticsTimelineEventsEnum.TAG],
    num_new_segments: [AnalyticsTimelineEventsEnum.TAG],
    num_messages_sent: [AnalyticsTimelineEventsEnum.TAG],
    num_messages_received: [AnalyticsTimelineEventsEnum.TAG],
    num_archived_segments: [
      AnalyticsTimelineEventsEnum.TAG,
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
    ],
    num_archived_segments_with_reply: [
      AnalyticsTimelineEventsEnum.TAG,
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
    ],
    num_active_segments: [AnalyticsTimelineEventsEnum.TAG],
    avg_reply_time: [AnalyticsTimelineEventsEnum.TAG],
    avg_reaction_time: [AnalyticsTimelineEventsEnum.TAG],
    avg_handle_time: [AnalyticsTimelineEventsEnum.TAG],
    avg_first_reply_time: [AnalyticsTimelineEventsEnum.TAG],
  },
  [AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE]: {
    num_removed: [AnalyticsTimelineEventsEnum.TAG, AnalyticsTimelineEventsEnum.UNTAG],
    num_added: [AnalyticsTimelineEventsEnum.TAG, AnalyticsTimelineEventsEnum.UNTAG],
    num_active_segments: [AnalyticsTimelineEventsEnum.TAG, AnalyticsTimelineEventsEnum.UNTAG],
    avg_tag_duration: [AnalyticsTimelineEventsEnum.TAG, AnalyticsTimelineEventsEnum.UNTAG],
  },
  [AnalyticsTableMetricsEnum.TEAM_CHANGES]: {
    average_reply_time: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
    average_handle_time: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
    average_first_reply_time: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
    archived_segments: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
    archived_segments_with_reply: [AnalyticsTimelineEventsEnum.ASSIGN, AnalyticsTimelineEventsEnum.UNASSIGN],
  },
  [AnalyticsTableMetricsEnum.TEAM_PERFORMANCE]: {
    // TODO: Apply tag only for breach tags.
    num_sla_breaches: [
      AnalyticsTimelineEventsEnum.UNASSIGN,
      AnalyticsTimelineEventsEnum.ASSIGN,
      AnalyticsTimelineEventsEnum.TAG,
    ],
    num_replies_sent: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    num_non_replies_sent: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    num_messages_sent: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    num_assigned_segments: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    num_archived_segments: [
      AnalyticsTimelineEventsEnum.UNASSIGN,
      AnalyticsTimelineEventsEnum.ASSIGN,
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
    ],
    num_archived_segments_with_reply: [
      AnalyticsTimelineEventsEnum.UNASSIGN,
      AnalyticsTimelineEventsEnum.ASSIGN,
      AnalyticsTimelineEventsEnum.ARCHIVE,
      AnalyticsTimelineEventsEnum.REOPEN,
    ],
    csat_survey_satisfaction: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_total_reply_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_reply_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_reaction_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_handle_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_first_reply_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
    avg_first_reaction_time: [AnalyticsTimelineEventsEnum.UNASSIGN, AnalyticsTimelineEventsEnum.ASSIGN],
  },
  [AnalyticsTableMetricsEnum.TOP_TAGS]: {},
  [AnalyticsTableMetricsEnum.WHAT_HAPPENED_TABLE]: {
    remaining_open: [AnalyticsTimelineEventsEnum.ARCHIVE, AnalyticsTimelineEventsEnum.REOPEN],
    new_segments: [AnalyticsTimelineEventsEnum.ARCHIVE, AnalyticsTimelineEventsEnum.REOPEN],
    closed_segments: [AnalyticsTimelineEventsEnum.ARCHIVE, AnalyticsTimelineEventsEnum.REOPEN],
  },
};

export const graphToShownEvents: GraphToShownEvents = {
  [AnalyticsGraphMetricsEnum.ACTIVE_SEGMENTS_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH]: [
    AnalyticsTimelineEventsEnum.ARCHIVE,
    AnalyticsTimelineEventsEnum.REOPEN,
  ],
  [AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.AVG_REPLIES_TO_RESOLUTION_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.AVG_RESOLUTION_TIME_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NUM_RESOLVED_SEGMENTS_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.NUM_UNRESOLVED_ACTIVE_SEGMENTS_GRAPH]: [],
  [AnalyticsGraphMetricsEnum.PCT_RESOLVED_ON_FIRST_REPLY_GRAPH]: [],
  // TODO: Apply only for breach tags.
  [AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH]: [AnalyticsTimelineEventsEnum.TAG],
  [AnalyticsGraphMetricsEnum.RESPONSE_GRAPH]: [],
};

export const heatmapToShownEvents: HeatmapToShownEvents = {
  [AnalyticsHeatMapMetricsEnum.INBOX]: {
    num_messages_sent: [AnalyticsTimelineEventsEnum.MOVE],
    num_messages_received: [AnalyticsTimelineEventsEnum.MOVE],
    num_new_segments: [AnalyticsTimelineEventsEnum.MOVE],
  },
};

export function computeShownEventsForMetric(
  metric: AnalyticsMetricsKeys,
  subMetric?: string,
): ReadonlyArray<AnalyticsTimelineEventsEnum> {
  if (RAnalyticsScalarMetricsEnum.is(metric)) {
    return scalarToShownEvents[metric];
  }

  if (RAnalyticsGraphMetricsEnum.is(metric)) {
    return graphToShownEvents[metric];
  }

  if (RAnalyticsTableMetricsEnum.is(metric)) {
    if (isUndefined(subMetric)) {
      return [];
    }
    return tableToShownEvents[metric][subMetric] ?? [];
  }

  if (RAnalyticsHeatMapMetricsEnum.is(metric)) {
    if (isUndefined(subMetric)) {
      return [];
    }
    return heatmapToShownEvents[metric][subMetric] ?? [];
  }

  return [];
}
